import { useEffect, useState } from "react";

import { ColumnsPhotoAlbum } from "react-photo-album";
import "react-photo-album/columns.css";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

import Lightbox from "yet-another-react-lightbox";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import GalleryNav from "./GalleryNav";
import IGLogo from "../../assets/global/ig-logo.png";

const GALLERY_CATEGORIES = [
  "3d-carved",
  "baby-shower",
  "birthdays",
  "bridal",
  "holidays",
  "kids",
  "succulents-florals",
] as const;

const galleryImagesContext = (require as any).context(
  "../../assets/gallery",
  true,
  /\.(png|jpe?g|webp)$/i,
);

const galleryPhotoSrcsByCategory = GALLERY_CATEGORIES.reduce(
  (acc, category) => {
    const srcs = galleryImagesContext
      .keys()
      .filter((path: string) => path.startsWith(`./${category}/`))
      .sort((a: string, b: string) => a.localeCompare(b))
      .map((path: string) => galleryImagesContext(path) as string);
    acc[category] = srcs;
    return acc;
  },
  {} as Record<(typeof GALLERY_CATEGORIES)[number], string[]>,
);

function Gallery() {
  const [index, setIndex] = useState(-1);
  const [selectedCategory, setSelectedCategory] =
    useState<(typeof GALLERY_CATEGORIES)[number]>("3d-carved");
  const [galleryPhotos, setGalleryPhotos] = useState(
    galleryPhotoSrcsByCategory["3d-carved"].map((src, photoIndex) => ({
      src,
      width: 4,
      height: 3,
      alt: `Gallery photo ${photoIndex + 1}`,
    })),
  );

  useEffect(() => {
    let isMounted = true;

    const loadPhotos = async () => {
      const sourceImages = galleryPhotoSrcsByCategory[selectedCategory] ?? [];
      const photosWithRealSize = await Promise.all(
        sourceImages.map(
          (src, photoIndex) =>
            new Promise<{
              src: string;
              width: number;
              height: number;
              alt: string;
            }>((resolve) => {
              const img = new window.Image();

              img.onload = () => {
                resolve({
                  src,
                  width: img.naturalWidth || 4,
                  height: img.naturalHeight || 3,
                  alt: `Gallery photo ${photoIndex + 1}`,
                });
              };

              img.onerror = () => {
                resolve({
                  src,
                  width: 4,
                  height: 3,
                  alt: `Gallery photo ${photoIndex + 1}`,
                });
              };

              img.src = src;
            }),
        ),
      );

      if (isMounted) {
        setGalleryPhotos(photosWithRealSize);
      }
    };

    void loadPhotos();

    return () => {
      isMounted = false;
    };
  }, [selectedCategory]);

  return (
    <div className="wrapper">
      <h1>Gallery of creations</h1>
      <GalleryNav
        selectedCategory={selectedCategory}
        onSelectCategory={(category) => {
          setSelectedCategory(category as (typeof GALLERY_CATEGORIES)[number]);
          setIndex(-1);
        }}
      />
      <div className="margin-bottom-sm">
        <a
          href="https://www.instagram.com/conniescakerie/"
          target="_blank"
          rel="noopener noreferrer"
          className="instagram-link"
          aria-label="View latest Instagram posts"
        >
          <img
            src={IGLogo}
            alt="Instagram logo"
            className="instagram-link__logo"
          />
          View latest Instagram posts
        </a>
      </div>
      <ColumnsPhotoAlbum
        photos={galleryPhotos}
        columns={(containerWidth) => {
          if (containerWidth < 600) return 2;
          if (containerWidth < 1024) return 3;
          return 4;
        }}
        onClick={({ index }) => setIndex(index)}
      />
      <Lightbox
        slides={galleryPhotos}
        open={index >= 0}
        index={index}
        close={() => setIndex(-1)}
        plugins={[Fullscreen, Slideshow, Thumbnails, Zoom]}
      />
    </div>
  );
}

export default Gallery;
